import React from "react";
import ReviewRating from "components/Reviews/ReviewRating.js";

import CustomLink from "components/../components/Links/CustomLink";
import ProductAthletesAvatarGroup from "components/ProductAthletes/ProductAthletesAvatarGroup";
import useTranslation from "next-translate/useTranslation";
import Trans from "next-translate/Trans";
import { TextAlert } from "components/Alert/TextAlert.js";
import { useLazyQuery } from "@apollo/client";

// core components
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";

import DismissableAlert from "components/Alert/DismissableAlert";
import LatestModelBadge from "components/Badge/LatestModelBadge";
import PriceBand from "components/PriceBand/PriceBand.js";
import { makeStyles } from "tss-react/mui";
import ArrowButton from "components/CustomButtons/ArrowButton";

import ExpertReviewScoreBadge from "components/Badge/ExpertReviewScoreBadge";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLayerGroup } from "@fortawesome/free-solid-svg-icons/faLayerGroup";

import { FAMILY_PRODUCTS_ID } from "settings/global-website-config";
import { useFullUser } from "../../auth/useUser";
import { USER_FOLLOWING_WITH_PRODUCT_QUERY } from "../../graphql/athlete.query";

const useStyles = makeStyles()((theme) => ({
  familyCount: { marginTop: theme.spacing(1) },
  statsContainer: {
    width: "100%",
    marginBottom: "7px",
    "& h6": {
      marginBottom: "0px",
    },
  },
}));

export const NewerModelBadge = (props) => {
  const { latestBaseModel, gridSizes, ...rest } = props;
  const { t } = useTranslation();

  return (
    <GridItem {...gridSizes}>
      <TextAlert
        heading={t("common:newerModel")}
        links={[
          {
            href: latestBaseModel.uri,
            targetTitle: latestBaseModel.noBrandTitle,
          },
        ]}
        {...rest}
      />
    </GridItem>
  );
};
export default function ProductInfoSection(props) {
  const {
    product,
    loading,
    gridSizes,
    type,
    showDetail,
    dealsLocale,
    family,
    userProduct,
  } = props;

  const { classes } = useStyles();
  const { t } = useTranslation();

  const [user, { handleUpdateUser, isUpdating }] = useFullUser();

  const [
    getFollowingWithProduct,
    { loading: lazyLoading, data: athleteSpecificData },
  ] = useLazyQuery(USER_FOLLOWING_WITH_PRODUCT_QUERY);

  React.useEffect(() => {
    if (
      user &&
      user.provider !== "wordpress" &&
      !athleteSpecificData &&
      !lazyLoading &&
      product
    ) {
      getFollowingWithProduct({
        variables: { id: user.databaseId, productIds: product.databaseId },
      });
    }
  }, [
    athleteSpecificData,
    getFollowingWithProduct,
    lazyLoading,
    product,
    user,
  ]);

  let productDetails,
    latestModelDetails,
    products,
    //family,
    priceBand,
    baseModel,
    specialVersions,
    isLatestModel,
    latestBaseModel,
    total;

  if (product) {
    ({
      productDetails,
      specialVersions,
      latestModelDetails,
      products,
      priceBand,
    } = product);

    if (productDetails) {
      ({ baseModel } = productDetails);
    }

    if (latestModelDetails) {
      ({ latestBaseModel, isLatestModel } = latestModelDetails);
    }
  }

  const familyToUse = product?.family || family || null;

  if (products) {
    ({
      offsetPagination: { total },
    } = products.pageInfo);
  }

  const equivalentFamilyProduct =
    showDetail &&
    type === "family" &&
    familyToUse?.products?.nodes.find((p) => p.title === product.title);

  return (
    !loading && (
      <>
        <GridContainer className={classes.statsContainer}>
          {showDetail &&
            familyToUse &&
            familyToUse.node?.title === product?.title && (
              <GridItem sx={{ mt: 1 }}>
                <DismissableAlert
                  product={product}
                  id="family-same-name"
                  title={t("product:familyOfSameName")}
                  severity="info"
                >
                  <Trans
                    i18nKey="product:seeBestPriceInFamily"
                    components={{
                      link: <CustomLink href={familyToUse.node.uri} />,
                      a: <a />,
                    }}
                    values={{ title: familyToUse.node.title }}
                  />
                </DismissableAlert>
              </GridItem>
            )}
          {equivalentFamilyProduct && (
            <GridItem sx={{ mt: 1 }}>
              <DismissableAlert
                product={product}
                id="product-same-name"
                title={t("product:productOfSameName")}
                severity="info"
              >
                <CustomLink href={equivalentFamilyProduct.uri}>
                  {equivalentFamilyProduct.title}
                </CustomLink>
              </DismissableAlert>
            </GridItem>
          )}
          {showDetail && product.productReviews?.reviewsScore > 0 && (
            <GridItem {...gridSizes}>
              <h6>{t("product:expertReviewScore")}</h6>
              <ExpertReviewScoreBadge
                reviews={!loading && product.productReviews}
                showScore
                loading={loading}
                href={product.uri}
                showLoadingSkeleton
                type={type}
                hideArrow
              />
            </GridItem>
          )}
          {showDetail && (
            <GridItem {...gridSizes}>
              <h6>{t("product:yourRating")}</h6>
              <ReviewRating
                productEntry={userProduct || { product }}
                user={user}
                //  setLoadingState={setLoadingState}
                handleUpdateUser={handleUpdateUser}
                isUpdating={isUpdating}
              />
            </GridItem>
          )}
          {priceBand && (
            <GridItem {...gridSizes}>
              <h6>{t("product:priceBand")}</h6>
              <PriceBand
                priceBand={priceBand.node.slug}
                dealsLocale={dealsLocale}
              />
            </GridItem>
          )}
          {familyToUse && familyToUse.node && (
            <GridItem {...gridSizes}>
              <h6>{t("product:family")}</h6>
              <ArrowButton
                href={familyToUse.node.uri}
                text={familyToUse.node.title}
                hideArrow
              />
            </GridItem>
          )}
          {showDetail && (
            <>
              {baseModel && (
                <GridItem {...gridSizes}>
                  <h6>{t("product:baseModel")}</h6>
                  <ArrowButton
                    href={baseModel?.uri}
                    text={baseModel?.title}
                    hideArrow
                  />
                </GridItem>
              )}
              {specialVersions && (
                <GridItem {...gridSizes}>
                  <h6>{t("product:specialVersions")}</h6>
                  <ul>
                    {specialVersions.nodes?.map((version) => (
                      <li key={`sv-${version.uri}`}>
                        <ArrowButton
                          href={version.uri}
                          text={version.noBrandTitle}
                          hideArrow
                        />
                      </li>
                    ))}
                  </ul>
                </GridItem>
              )}
            </>
          )}
          {product.productAthletes?.edges?.length ? (
            <GridItem {...gridSizes}>
              <ProductAthletesAvatarGroup
                productAthletes={product.productAthletes}
                href={showDetail ? product.uri : null}
                showTitle
                max={5}
                loading={loading}
                left
              />
            </GridItem>
          ) : null}
          {athleteSpecificData?.athlete?.followingWithProduct && (
            <GridItem {...gridSizes}>
              <ProductAthletesAvatarGroup
                productAthletes={
                  athleteSpecificData?.athlete?.followingWithProduct
                }
                href={showDetail ? product.uri : null}
                showTitle
                max={5}
                loading={loading}
                left
                athleteType="following"
              />
            </GridItem>
          )}
          {isLatestModel ? (
            <GridItem {...gridSizes}>
              <LatestModelBadge />
            </GridItem>
          ) : (
            !baseModel &&
            latestBaseModel && (
              <NewerModelBadge
                latestBaseModel={latestBaseModel}
                {...gridSizes}
              />
            )
          )}
          {products && (
            <GridItem {...gridSizes} className={classes.familyCount}>
              <ArrowButton
                icon={<FontAwesomeIcon icon={faLayerGroup} />}
                href={`${product.uri}#${FAMILY_PRODUCTS_ID}`}
                text={t("product:familyModels", { total })}
                hideArrow
              />
            </GridItem>
          )}
        </GridContainer>
      </>
    )
  );
}
